<template>
    <div id="email-login-form">
        <div v-if="error" class="error-message" :class="errorType" :innerText="error"></div>

        <Loader v-if="isProcessing"></Loader>

        <form v-else-if="ssoButtonCallback">
            <button @click="ssoButtonCallback" class="primary">Continue with Single Sign On</button>
            <button @click="ssoButtonCallback = undefined">Cancel</button>
        </form>

        <form v-else ref="form" @submit.prevent="login">
            <input v-model="email" v-autofocus="!enablePassword" type="email" placeholder="e-mail address" />
            <input v-if="enablePassword" v-model="password" v-autofocus type="password" placeholder="password" />
            <button class="primary">Continue</button>

            <template v-if="!enablePassword">
                <button type="button" class="sso sso-microsoft mt-2" @click="startSsoLogin({ provider: 'azure' })">
                    <img src="/assets/login-ms.svg" /> Sign in with Microsoft
                </button>
                <button type="button" class="sso sso-google" @click="startSsoLogin({ provider: 'google' })">
                    <img src="/assets/login-google.svg" /> Sign in with Google
                </button>
            </template>

            <label class="remember-me" :class="{ active: rememberMe }">
                <input type="checkbox" v-model="rememberMe" />
                <i :class="rememberMe ? 'fa-solid fa-circle-check' : 'fa-light fa-circle'"></i>
                Keep me signed in for 30 days
            </label>
        </form>
    </div>
</template>

<script lang="ts" setup>
import { formatError, handleError, maskForm, unmaskForm } from '@signal24/vue-foundation';
import { ref } from 'vue';

import { GlobalState } from '@/global';
import { LoginApi } from '@/openapi-clients-generated/auth';
import Loader from '@/shared/components/loader.vue';
import { useBroadcastMessageMonitor } from '@/shared/helpers/broadcast.helpers';
import { Auth } from '@/shared/services/auth.service';
import { dataFrom } from '@signal24/openapi-client-codegen';

const form = ref<HTMLFormElement>();
const error = ref<null | string>(null);
const errorType = ref<string | null>();
const email = ref('');
const password = ref('');

const isProcessing = ref(false);
const ssoButtonCallback = ref<() => void>();
const enablePassword = ref(false);
const rememberMe = ref(false);

const msgMonitor = useBroadcastMessageMonitor('login');

function clearError() {
    errorType.value = null;
    error.value = null;
}

async function login() {
    if (!email.value.trim().length) {
        return;
    }
    if (enablePassword.value && !password.value.length) {
        return;
    }

    maskForm(form.value!);
    clearError();

    try {
        if (enablePassword.value) {
            const { data } = await LoginApi.postLoginPasswordLogin<true>({
                body: {
                    browserId: GlobalState.browserId,
                    email: email.value,
                    password: password.value,
                    rememberMe: rememberMe.value
                },
                credentials: 'include'
            });
            await Auth.processLoginResponse(data);
        } else {
            const response = dataFrom(
                await LoginApi.postLoginLogin({
                    body: { email: email.value, redirectUrl: `${location.origin}/login/callback` }
                })
            );
            if (response.authorizeUrl) {
                startSsoLogin({ authorizeUrl: response.authorizeUrl });
            } else {
                unmaskForm(form.value!);
                enablePassword.value = true;
            }
        }
    } catch (err) {
        unmaskForm(form.value!);
        handleLoginError(err);
    }
}

async function startSsoLogin({ provider, authorizeUrl }: { provider?: 'azure' | 'google'; authorizeUrl?: string }) {
    clearError();

    const loginWindow = window.open(
        authorizeUrl ?? `/login/sso?provider=${provider}`,
        'loginSSO',
        'width=450,height=550,popup=true,menubar=no,toolbar=no,location=no,status=no'
    );

    if (!loginWindow) {
        ssoButtonCallback.value = () => {
            ssoButtonCallback.value = undefined;
            startSsoLogin({ provider, authorizeUrl });
        };
        return;
    }

    isProcessing.value = true;

    let windowClosed = false;
    const windowClosePromise = new Promise<void>(resolve => {
        const interval = setInterval(() => {
            try {
                if (loginWindow.closed) {
                    windowClosed = true;
                    clearInterval(interval);
                    resolve();
                }
            } catch {
                /**/
            }
        }, 500);
    });
    const messagePromise = msgMonitor.waitForMessage<Record<string, string>>();
    await Promise.race([windowClosePromise, messagePromise]);

    // if the window was closed, we don't need to do anything
    if (windowClosed) {
        isProcessing.value = false;
        return;
    }

    const urlParams = await messagePromise;
    if (!urlParams) {
        return handleLoginError('Missing callback parameters', true);
    }

    if (urlParams.error) {
        return handleLoginError(`SSO error: ${urlParams.error}: ${urlParams.error_description}`, true);
    }

    if (!urlParams.code) {
        return handleLoginError('Missing code parameter', true);
    }

    const code = urlParams.code!;
    const state = urlParams.state!;

    // clear any existing session so that it's not included in the login request
    Auth.clearStoredJwt();

    try {
        const { data } = await LoginApi.postLoginSsoCallback<true>({
            body: {
                browserId: GlobalState.browserId,
                redirectUrl: `${location.origin}/login/callback`,
                code,
                state,
                rememberMe: rememberMe.value
            },
            credentials: 'include'
        });
        await Auth.processLoginResponse(data);
    } catch (err) {
        return handleLoginError(err);
    }
}

// eslint-disable-next-line no-explicit-any
function handleLoginError(err: any, isCritical?: boolean) {
    if (err instanceof Error || isCritical) {
        handleError(err);
        error.value = String(err).includes('invalid_credentials')
            ? 'The e-mail address or password you provided is incorrect. Please try again. If you need further assistance, please contact support@zyno.app'
            : formatError(err);
    } else {
        error.value = err;
    }

    errorType.value = null;
    isProcessing.value = false;
}

const sessionLogoutCause = Auth.logoutCauseOnce;
if (sessionLogoutCause) {
    error.value = sessionLogoutCause;
    errorType.value = 'warning';
}
</script>

<style scoped>
@reference '#tw.css';

#email-login-form {
    @apply max-w-xs w-screen;
}

form {
    @apply flex flex-col gap-1;
}

.sso {
    @apply bg-white border-gray-300 hover:bg-gray-200 flex gap-2;

    img {
        @apply max-w-6;
    }
}

.remember-me {
    @apply mt-2 flex items-center gap-2 text-slate-500 cursor-pointer hover:bg-slate-100 border border-slate-200 rounded-md p-1 justify-center text-sm;

    &.active {
        @apply text-blue-800 bg-blue-100;
    }

    input {
        @apply hidden;
    }

    i {
        @apply text-base;
    }
}
</style>
